/**
 * The mandatory sensitive-field pattern (docs/03 §4): masked dots + Reveal →
 * permission-checked fetch → value for one render with the audit microcopy →
 * auto-re-mask after 8 seconds or on blur/unmount. The value is never persisted
 * client-side, never in props from a list payload — it arrives only from the
 * reveal endpoint and lives only in transient state.
 */

import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

const REMASK_MS = 8000;

export interface MaskedFieldProps {
  readonly label: string;
  /** Fetches the value via the audited reveal endpoint (docs/01 §5). */
  readonly onReveal: () => Promise<string>;
  /** Absent permission renders the disabled-with-reason affordance. */
  readonly canReveal: boolean;
}

export function MaskedField({ label, onReveal, canReveal }: MaskedFieldProps): ReactNode {
  const [value, setValue] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const remask = (): void => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    setValue(null);
  };

  // Re-mask on unmount/navigation, always.
  useEffect(() => remask, []);

  const reveal = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const revealed = await onReveal();
      setValue(revealed);
      timer.current = setTimeout(remask, REMASK_MS);
    } catch {
      setError('Could not reveal this value. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-1" onBlur={remask}>
      <span className="text-meta font-medium text-ink2">{label}</span>
      <div className="flex items-center gap-2">
        {value === null ? (
          <span aria-label={`${label} is hidden`} className="tracking-widest text-ink">
            ••••••••
          </span>
        ) : (
          <output className="font-medium text-ink">{value}</output>
        )}
        <button
          type="button"
          onClick={value === null ? () => void reveal() : remask}
          disabled={!canReveal || busy}
          title={canReveal ? undefined : 'Requires the view-access-codes permission'}
          aria-label={value === null ? `Reveal ${label}` : `Hide ${label}`}
          className="flex min-h-11 min-w-11 items-center justify-center rounded-ctl text-ink2 hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {value === null ? <Eye size={18} aria-hidden /> : <EyeOff size={18} aria-hidden />}
        </button>
      </div>
      {value !== null ? (
        <span role="status" className="text-chip text-ink2">
          Reveal recorded in the audit ledger
        </span>
      ) : null}
      {error !== null ? (
        <span role="alert" className="text-chip text-red-fg">
          {error}
        </span>
      ) : null}
    </div>
  );
}
